import { type Event, type TimesheetLine, type User } from '@kalos/kalos-rpc';
import { useMemo } from 'react';

import { useTimesheetLineBatchGetQuery } from '../../../../hooks/react-query/useTimesheetQuery';
import { useBatchUserQuery } from '../../../../hooks/react-query/useUserQuery';

/**
 * What a past visit's detail shows beyond the event itself: labor hours from its timesheet lines,
 * the assigned technicians by name, and the equipment it serviced. Shared by the desktop row
 * and the phone's visit sheet.
 */
export const useJobHistoryFacts = (event: Event, filterServiceItemId?: number) => {
  const { data: timesheetLinesData } = useTimesheetLineBatchGetQuery({
    filter: {
      eventId: event.id,
      isActive: 1,
      withoutLimit: true,
    },
    enabled: !!event.id,
  });

  const { data: usersData } = useBatchUserQuery({
    filters: {
      isEmployee: 1,
      overrideLimit: true,
    },
    enabled: true,
  });

  const laborHours = useMemo(
    () =>
      timesheetLinesData?.results.reduce(
        (acc: number, line: TimesheetLine) => acc + (line.duration || 0),
        0,
      ) || 0,
    [timesheetLinesData],
  );

  const technicians = useMemo(() => {
    const techIds = (event.logTechnicianAssigned || '')
      .split(',')
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !isNaN(id));

    if (!usersData?.results) return 'N/A';

    const techNames = techIds
      .map((id) => {
        const user = usersData.results.find((u: User) => u.id === id);
        return user ? `${user.firstname} ${user.lastname}` : null;
      })
      .filter((name) => name)
      .join(', ');

    return techNames || 'N/A';
  }, [usersData, event.logTechnicianAssigned]);

  const serviceItemIds = useMemo(() => {
    if (!event.invoiceServiceItem) return [];
    const ids = event.invoiceServiceItem.split(',');
    if (ids.length === 1 && ids[0] === '') return [];
    const parsed = ids.map((id) => parseInt(id.trim(), 10)).filter((id) => !isNaN(id));
    if (filterServiceItemId && filterServiceItemId > 0) {
      return parsed.filter((id) => id === filterServiceItemId);
    }
    return parsed;
  }, [event.invoiceServiceItem, filterServiceItemId]);

  return { laborHours, technicians, serviceItemIds };
};
